import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Package, Wallet, FileWarning, TrendingUp, TrendingDown, Minus, Target as TargetIcon, ArrowRight, Flame, Truck, Scale,
  ArrowDownToLine, ArrowUpFromLine, BookOpen, Fuel, CalendarClock, Weight, Receipt, ClipboardList,
} from "lucide-react";
import { formatMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";

const compactMoney = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 100000)  return `${(v / 100000).toFixed(2)} L`;
  if (Math.abs(v) >= 1000)    return `${(v / 1000).toFixed(1)} K`;
  return `${Math.round(v)}`;
};

const dayLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
};

const monthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

function TrendBadge({ trend }) {
  if (!trend) return null;
  const { direction, delta_pct } = trend;
  if (direction === "flat") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold bg-gray-100 text-gray-600" data-testid="sales-trend-badge">
        <Minus size={11} /> Flat vs last month
      </span>
    );
  }
  const up = direction === "up";
  const cls = up ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#B91C1C]";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold ${cls}`} data-testid="sales-trend-badge">
      <Icon size={11} /> {up ? "Growing" : "Falling"} {up ? "+" : ""}{delta_pct}% vs last month
    </span>
  );
}

function SalesTrendCard({ data }) {
  if (!data) {
    return (
      <div className="card-flat p-5" data-testid="sales-trend-card">
        <div className="small-label text-gray-500">Sales Trend</div>
        <div className="text-sm text-gray-400 mt-2">Loading sales analytics…</div>
      </div>
    );
  }

  const { last_7_days = [], last_month = {}, this_month = {}, target = {}, trend = {} } = data;
  const chartData = last_7_days.map((d) => ({ ...d, label: dayLabel(d.date) }));
  const achievedClamped = Math.min(target.achieved_pct || 0, 100);
  const isBehind = (target.achieved_pct || 0) < 100 * ((this_month.days_elapsed || 0) / (this_month.days_in_month || 1));

  return (
    <div className="card-flat p-5 space-y-5" data-testid="sales-trend-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="small-label text-gray-500">Sales Analytics</div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <h3 className="font-heading font-black text-xl">Last 7 days</h3>
            <TrendBadge trend={trend} />
            {data.as_of_date && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold bg-gray-100 text-gray-600" data-testid="sales-as-of">
                As of end of {new Date(data.as_of_date + "T00:00:00").toLocaleDateString("en-US", { day: "2-digit", month: "short" })} · today excluded
              </span>
            )}
          </div>
        </div>
        <Link to="/performance" className="small-label text-gray-700 inline-flex items-center gap-1 hover:text-black" data-testid="sales-trend-perf-link">
          Deep dive <ArrowRight size={12} />
        </Link>
      </div>

      <div className="h-52 w-full" data-testid="sales-trend-chart">
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#eee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#e5e5e5" }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => compactMoney(v)} tickLine={false} axisLine={false} width={55} />
            <Tooltip
              cursor={{ fill: "rgba(0,0,0,0.04)" }}
              contentStyle={{ borderRadius: 2, border: "1px solid #e5e5e5", fontSize: 12 }}
              formatter={(v) => [`\u20B9 ${formatMoney(v)}`, "Sales"]}
              labelFormatter={(l, p) => (p && p[0] ? p[0].payload.date : l)}
            />
            <ReferenceLine y={target.per_day} stroke="#B45309" strokeDasharray="3 3" label={{ value: `Target · ${compactMoney(target.per_day)}/day`, position: "insideTopRight", fontSize: 10, fill: "#B45309" }} />
            <Bar dataKey="amount" fill="#0A0A0A" radius={[2, 2, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-last-month-avg">
          <div className="small-label text-gray-500">Avg / day · {monthLabel(last_month.label)}</div>
          <div className="data-num text-lg font-bold mt-1">₹ {formatMoney(last_month.avg_per_day || 0)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">Total ₹ {compactMoney(last_month.total)} · {last_month.days || 0}d</div>
        </div>
        <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-this-month-avg">
          <div className="small-label text-gray-500">Avg / day · {monthLabel(this_month.label)}</div>
          <div className="data-num text-lg font-bold mt-1">₹ {formatMoney(this_month.avg_per_day || 0)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            Total ₹ {compactMoney(this_month.total)} · {this_month.days_elapsed || 0} of {this_month.days_in_month || 0}d
          </div>
        </div>
        <div className="border border-[#FDE68A] rounded-sm p-3 bg-[#FEF3C7]/40" data-testid="kpi-target-month">
          <div className="small-label text-[#B45309] flex items-center gap-1"><TargetIcon size={11} /> Target · this month</div>
          <div className="data-num text-lg font-bold mt-1">₹ {formatMoney(target.monthly || 0)}</div>
          <div className="text-[10px] text-[#B45309] mt-0.5">= last month + 5% · ₹ {compactMoney(target.per_day)}/day</div>
        </div>
        <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-target-progress">
          <div className="small-label text-gray-500">Target achieved</div>
          <div className={`data-num text-lg font-bold mt-1 ${isBehind ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
            {(target.achieved_pct || 0).toFixed(1)}%
          </div>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full ${isBehind ? "bg-[#B91C1C]" : "bg-[#15803D]"}`} style={{ width: `${achievedClamped}%` }} />
          </div>
          <div className="text-[10px] text-gray-500 mt-1">Projected ₹ {compactMoney(this_month.projected)}</div>
        </div>
      </div>

      {/* Pro-rata (till date) target & fuel */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-prorata-target">
          <div className="small-label text-gray-500 flex items-center gap-1"><CalendarClock size={11} /> Target · till today</div>
          <div className="data-num text-lg font-bold mt-1">₹ {formatMoney(target.pro_rata_expected || 0)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            = ₹ {compactMoney(target.per_day)} × {this_month.days_elapsed || 0} days
          </div>
        </div>
        <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-prorata-progress">
          <div className="small-label text-gray-500">Achieved vs till-date target</div>
          <div className={`data-num text-lg font-bold mt-1 ${(target.pro_rata_pct || 0) < 100 ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
            {(target.pro_rata_pct || 0).toFixed(1)}%
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            ₹ {compactMoney(this_month.total)} of ₹ {compactMoney(target.pro_rata_expected)}
          </div>
        </div>
        <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-fuel-this-month">
          <div className="small-label text-gray-500 flex items-center gap-1"><Fuel size={11} /> Fuel · this month</div>
          <div className="data-num text-lg font-bold mt-1 text-[#B45309]">₹ {formatMoney(this_month.diesel_total || 0)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">Diesel booked so far</div>
        </div>
      </div>

      {/* KG snapshot — total LPG sold across all brands & sizes */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Weight size={14} className="text-gray-700" />
            <div className="small-label text-gray-500">Total LPG sold · all brands & sizes combined</div>
          </div>
          <div className="text-[10px] text-gray-400">
            kg = Σ (filled qty × cylinder size)
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-kg-last-month">
            <div className="small-label text-gray-500">Last month · {monthLabel(last_month.label)}</div>
            <div className="data-num text-lg font-bold mt-1">{formatMoney(last_month.kg_total || 0)} <span className="text-xs text-gray-500 font-normal">kg</span></div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Avg {formatMoney(last_month.avg_kg_per_day || 0)} kg/day · {last_month.days || 0}d
            </div>
          </div>
          <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-kg-this-month">
            <div className="small-label text-gray-500">This month · {monthLabel(this_month.label)}</div>
            <div className="data-num text-lg font-bold mt-1">{formatMoney(this_month.kg_total || 0)} <span className="text-xs text-gray-500 font-normal">kg</span></div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Avg {formatMoney(this_month.avg_kg_per_day || 0)} kg/day · {this_month.days_elapsed}/{this_month.days_in_month}d
            </div>
          </div>
          <div className="border border-[#FDE68A] rounded-sm p-3 bg-[#FEF3C7]/40" data-testid="kpi-kg-target">
            <div className="small-label text-[#B45309] flex items-center gap-1"><TargetIcon size={11} /> Target · kg / month</div>
            <div className="data-num text-lg font-bold mt-1">{formatMoney(target.kg_monthly || 0)} <span className="text-xs text-[#B45309] font-normal">kg</span></div>
            <div className="text-[10px] text-[#B45309] mt-0.5">
              = last month + 5% · {formatMoney(target.kg_per_day || 0)} kg/day
            </div>
          </div>
          <div className="border border-gray-200 rounded-sm p-3" data-testid="kpi-kg-progress">
            <div className="small-label text-gray-500">Kg target achieved</div>
            <div className={`data-num text-lg font-bold mt-1 ${(target.kg_achieved_pct || 0) < 100 * ((this_month.days_elapsed || 0) / (this_month.days_in_month || 1)) ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
              {(target.kg_achieved_pct || 0).toFixed(1)}%
            </div>
            <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${(target.kg_achieved_pct || 0) < 100 * ((this_month.days_elapsed || 0) / (this_month.days_in_month || 1)) ? "bg-[#B91C1C]" : "bg-[#15803D]"}`}
                style={{ width: `${Math.min(target.kg_achieved_pct || 0, 100)}%` }} />
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              Projected {formatMoney(this_month.projected_kg || 0)} kg
            </div>
          </div>
        </div>
      </div>

      {/* Past monthly snapshot */}
      {Array.isArray(data.monthly_series) && data.monthly_series.length > 0 && (
        <div className="border-t border-gray-100 pt-4" data-testid="monthly-series-block">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Past monthly snapshot · sales vs fuel</div>
            <div className="text-[10px] text-gray-400">Current month uses days elapsed</div>
          </div>
          <div className="table-wrap">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Month</th>
                  <th className="num">Sales ₹</th>
                  <th className="num">Sales KG</th>
                  <th className="num text-[#1D4ED8]">Purchase KG</th>
                  <th className="num text-[#B45309]">Fuel ₹</th>
                  <th className="num">Net after fuel</th>
                  <th className="num">Sales count</th>
                  <th className="num">Avg / day</th>
                </tr>
              </thead>
              <tbody>
                {data.monthly_series.map((m) => {
                  const fuelRatio = m.sales_total > 0 ? (m.diesel_total / m.sales_total) * 100 : 0;
                  return (
                    <tr key={m.label} className={m.is_current ? "bg-[#FEF3C7]/40" : ""} data-testid={`month-row-${m.label}`}>
                      <td>
                        <div className="font-semibold">{monthLabel(m.label)}</div>
                        {m.is_current && <div className="text-[9px] font-bold text-[#B45309] tracking-wider">CURRENT · {m.days_counted}/{m.days}d</div>}
                      </td>
                      <td className="num data-num font-bold">₹ {formatMoney(m.sales_total)}</td>
                      <td className="num data-num">
                        {formatMoney(m.sales_kg || 0)} <span className="text-[10px] text-gray-500">kg</span>
                        {m.avg_kg_per_day > 0 && <div className="text-[9px] text-gray-400">{formatMoney(m.avg_kg_per_day)} kg/day</div>}
                      </td>
                      <td className="num data-num text-[#1D4ED8]">
                        {formatMoney(m.purchase_kg || 0)} <span className="text-[10px] text-gray-500">kg</span>
                        {m.purchase_count > 0 && <div className="text-[9px] text-gray-400">{m.purchase_count} lots</div>}
                      </td>
                      <td className="num data-num text-[#B45309]">
                        ₹ {formatMoney(m.diesel_total)}
                        {fuelRatio > 0 && <div className="text-[9px] text-gray-400">{fuelRatio.toFixed(2)}% of sales</div>}
                      </td>
                      <td className="num data-num">{formatMoney(m.net_after_fuel)}</td>
                      <td className="num text-xs text-gray-500">{m.count}</td>
                      <td className="num data-num text-xs">₹ {compactMoney(m.avg_per_day)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Brand-wise KG matrix (sales & purchases per month) */}
      {Array.isArray(data.brand_matrix) && data.brand_matrix.length > 0 && (
        <div className="border-t border-gray-100 pt-4" data-testid="brand-matrix-block">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="small-label text-gray-500">Brand-wise KG · sales & purchases per month</div>
            <div className="flex items-center gap-3 text-[10px] text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-black"></span> Sales kg</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#1D4ED8]"></span> Purchase kg</span>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table-dense w-full min-w-[700px]">
              <thead>
                <tr>
                  <th className="text-left sticky left-0 bg-white z-10">Brand</th>
                  {(data.series_months || []).map((ym) => (
                    <th key={ym} className="num text-center min-w-[110px]">
                      <div className="uppercase tracking-wider text-[10px] font-black">{monthLabel(ym)}</div>
                      {ym === data.this_month?.label && (
                        <div className="text-[9px] font-bold text-[#B45309] tracking-wider">CURRENT</div>
                      )}
                    </th>
                  ))}
                  <th className="num text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.brand_matrix.map((b) => (
                  <tr key={b.brand} data-testid={`brand-row-${b.brand}`}>
                    <td className="sticky left-0 bg-white font-semibold">{b.brand}</td>
                    {(data.series_months || []).map((ym) => {
                      const c = b.cells?.[ym] || {};
                      const hasData = (c.sales_kg || 0) > 0 || (c.purchase_kg || 0) > 0;
                      return (
                        <td key={ym} className={`num align-middle ${ym === data.this_month?.label ? "bg-[#FEF3C7]/40" : ""}`}>
                          {hasData ? (
                            <div className="leading-tight">
                              <div className="data-num font-bold">{formatMoney(c.sales_kg)}</div>
                              <div className="data-num text-[10px] text-[#1D4ED8]">↳ {formatMoney(c.purchase_kg)}</div>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="num text-right">
                      <div className="data-num font-black">{formatMoney(b.total_sales_kg)}</div>
                      <div className="data-num text-[10px] text-[#1D4ED8]">↳ {formatMoney(b.total_purchase_kg)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            Each cell shows two figures — top black = kg <b>sold</b> in that month for that brand; blue arrow line = kg <b>purchased</b>. KG is derived from filled-cylinder qty × size.
          </p>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [stock, setStock] = useState({});
  const [pendingPay, setPendingPay] = useState(null);
  const [pendingCyl, setPendingCyl] = useState(null);
  const [pendingMt, setPendingMt] = useState(0);
  const [pendingTally, setPendingTally] = useState([]);
  const [txns, setTxns] = useState([]);
  const [godownList, setGodownList] = useState([]);
  const [totalsQty, setTotalsQty] = useState(null);
  const [bulletStock, setBulletStock] = useState({});
  const [registerSummary, setRegisterSummary] = useState(null);
  const [pendingBillsSummary, setPendingBillsSummary] = useState(null);
  const [payVerify, setPayVerify] = useState(null);
  const [checklistPending, setChecklistPending] = useState(null);
  const [salesTrend, setSalesTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [s, pp, pc, t, g, pmt, pt, tot, bs, rs, st, pb, pv, cl] = await Promise.all([
          api.get("/stock/live"),
          api.get("/pending/payments"),
          api.get("/pending/cylinders"),
          api.get("/transactions?limit=10"),
          api.get("/godowns"),
          api.get("/pending/mt"),
          api.get("/tally/pending-dates"),
          api.get("/totals/qty"),
          api.get("/stock/bullet"),
          api.get("/register-entries/summary").catch(() => ({ data: null })),
          api.get("/dashboard/sales-trend").catch(() => ({ data: null })),
          api.get("/pending-bills?filter=all").catch(() => ({ data: null })),
          api.get("/payments/unverified-summary").catch(() => ({ data: null })),
          api.get(`/checklist/day?date=${today}`).catch(() => ({ data: null })),
        ]);
        setStock(s.data);
        setPendingPay(pp.data);
        setPendingCyl(pc.data);
        setTxns(t.data);
        setGodownList(g.data.map((x) => x.name));
        setPendingMt(pmt.data.count || 0);
        setPendingTally(pt.data.pending || []);
        setTotalsQty(tot.data);
        setBulletStock(bs.data || {});
        setRegisterSummary(rs.data);
        setSalesTrend(st.data);
        setPendingBillsSummary(pb.data ? pb.data.summary : null);
        setPayVerify(pv.data);
        if (cl.data && cl.data.summary) {
          const total = cl.data.summary.reduce((acc, x) => acc + x.total, 0);
          const done = cl.data.summary.reduce((acc, x) => acc + x.done, 0);
          const pendingByPerson = cl.data.summary
            .filter((x) => x.done < x.total)
            .map((x) => `${x.person} (${x.total - x.done})`);
          setChecklistPending({ total, done, pending: total - done, pendingByPerson });
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const allGodownNames = Array.from(new Set([...godownList, ...Object.keys(stock)]));
  const godowns = allGodownNames.sort();

  // aggregate totals per godown
  const totals = godowns.map((g) => {
    let filled = 0, mt = 0;
    const gObj = stock[g] || {};
    for (const brand of Object.keys(gObj)) {
      for (const size of Object.keys(gObj[brand])) {
        filled += gObj[brand][size].filled;
        mt += gObj[brand][size].mt;
      }
    }
    return { godown: g, filled, mt };
  });

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {pendingTally.length > 0 && (
        <Link to="/tally" className="block card-flat p-4 bg-[#FEE2E2] border-[#FECACA] hover:bg-[#FECACA] transition" data-testid="pending-tally-banner">
          <div className="flex items-center gap-3">
            <Scale size={24} className="text-[#B91C1C]" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B91C1C]">Blocking — entries disabled</div>
              <div className="font-heading font-bold text-lg truncate">
                Daily tally pending for {pendingTally.length} day{pendingTally.length === 1 ? "" : "s"}
              </div>
              <div className="text-xs text-gray-700 truncate">
                {pendingTally.slice(0, 3).map((p) => p.date).join(", ")}
                {pendingTally.length > 3 ? " …" : ""}
              </div>
            </div>
            <ArrowRight size={18} className="text-[#B91C1C]" />
          </div>
        </Link>
      )}
      {pendingMt > 0 && (
        <Link to="/pending/mt" className="block card-flat p-4 bg-[#FEF3C7] border-[#FDE68A] hover:bg-[#FDE68A] transition" data-testid="pending-mt-banner">
          <div className="flex items-center gap-3">
            <Truck size={24} className="text-[#B45309]" strokeWidth={2.5} />
            <div className="flex-1">
              <div className="small-label text-[#B45309]">Attention · In-transit vehicles</div>
              <div className="font-heading font-bold text-lg">
                {pendingMt} sale{pendingMt === 1 ? "" : "s"} awaiting MT cylinder entry
              </div>
            </div>
            <ArrowRight size={18} className="text-[#B45309]" />
          </div>
        </Link>
      )}
      {pendingBillsSummary && pendingBillsSummary.mismatch_count > 0 && (
        <Link to="/pending/bills" className="block card-flat p-4 bg-[#FEE2E2] border-[#FECACA] hover:bg-[#FECACA] transition" data-testid="bill-mismatch-banner">
          <div className="flex items-center gap-3">
            <Receipt size={24} className="text-[#B91C1C]" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B91C1C]">Billing · Amount mismatch</div>
              <div className="font-heading font-bold text-lg truncate">
                {pendingBillsSummary.mismatch_count} bill{pendingBillsSummary.mismatch_count === 1 ? "" : "s"} where bill amount ≠ ledger amount
              </div>
              <div className="text-xs text-gray-700">Verify bills &amp; correct the amounts</div>
            </div>
            <ArrowRight size={18} className="text-[#B91C1C]" />
          </div>
        </Link>
      )}
      {pendingBillsSummary && pendingBillsSummary.pending_count > 0 && (
        <Link to="/pending/bills" className="block card-flat p-4 bg-[#FEF3C7] border-[#FDE68A] hover:bg-[#FDE68A] transition" data-testid="pending-bills-banner">
          <div className="flex items-center gap-3">
            <FileWarning size={24} className="text-[#B45309]" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B45309]">Billing · Pending entry</div>
              <div className="font-heading font-bold text-lg truncate">
                {pendingBillsSummary.pending_count} challan{pendingBillsSummary.pending_count === 1 ? "" : "s"} pending bill entry
              </div>
              <div className="text-xs text-gray-700">Add bill number &amp; amount to close these</div>
            </div>
            <ArrowRight size={18} className="text-[#B45309]" />
          </div>
        </Link>
      )}
      {checklistPending && checklistPending.pending > 0 && (
        <Link to="/checklist" className="block card-flat p-4 bg-[#FEF3C7] border-[#FDE68A] hover:bg-[#FDE68A] transition" data-testid="checklist-pending-banner">
          <div className="flex items-center gap-3">
            <ClipboardList size={24} className="text-[#B45309]" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B45309]">Team Checklist · Today</div>
              <div className="font-heading font-bold text-lg truncate">
                {checklistPending.pending} of {checklistPending.total} checklist item{checklistPending.pending === 1 ? "" : "s"} still pending today
              </div>
              <div className="text-xs text-gray-700 truncate">
                {checklistPending.pendingByPerson.slice(0, 4).join(" · ")}
                {checklistPending.pendingByPerson.length > 4 ? " …" : ""}
              </div>
            </div>
            <ArrowRight size={18} className="text-[#B45309]" />
          </div>
        </Link>
      )}
      {payVerify && payVerify.count > 0 && (
        <Link to="/payments-log" className="block card-flat p-4 bg-[#FEF3C7] border-[#FDE68A] hover:bg-[#FDE68A] transition" data-testid="pay-verify-banner">
          <div className="flex items-center gap-3">
            <Wallet size={24} className="text-[#B45309]" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B45309]">Payments · Verification pending</div>
              <div className="font-heading font-bold text-lg truncate">
                {payVerify.count} payment{payVerify.count === 1 ? "" : "s"} awaiting bank verification · ₹ {formatMoney(payVerify.total_amount)}
              </div>
              <div className="text-xs text-gray-700">
                Bank / UPI / Cheque payments not yet matched to statement
                {payVerify.oldest_date ? ` · oldest ${payVerify.oldest_date}` : ""}
              </div>
            </div>
            <ArrowRight size={18} className="text-[#B45309]" />
          </div>
        </Link>
      )}
      {registerSummary && registerSummary.problem_count > 0 && (
        <Link to="/register" className="block card-flat p-4 bg-[#FEE2E2] border-[#FECACA] hover:bg-[#FECACA] transition" data-testid="register-mismatch-banner">
          <div className="flex items-center gap-3">
            <BookOpen size={24} className="text-[#B91C1C]" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B91C1C]">Physical Register · Attention</div>
              <div className="font-heading font-bold text-lg truncate">
                {registerSummary.problem_count} register entr{registerSummary.problem_count === 1 ? "y" : "ies"} don&apos;t tally with system
              </div>
              <div className="text-xs text-gray-700">
                {registerSummary.mismatch > 0 && <span><b>{registerSummary.mismatch}</b> qty mismatch</span>}
                {registerSummary.mismatch > 0 && registerSummary.unmatched > 0 && " · "}
                {registerSummary.unmatched > 0 && <span><b>{registerSummary.unmatched}</b> not found in system</span>}
              </div>
            </div>
            <ArrowRight size={18} className="text-[#B91C1C]" />
          </div>
        </Link>
      )}
      {/* Top KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {totals.map((t) => (
          <div key={t.godown} className="card-flat p-5" data-testid={`kpi-godown-${t.godown.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="small-label text-gray-500">Godown</div>
                <div className="font-heading font-bold text-xl mt-1">{t.godown}</div>
              </div>
              <Package size={22} strokeWidth={2.5} className="text-gray-400" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="small-label text-gray-500">Filled</div>
                <div className="data-num text-2xl sm:text-3xl text-[#15803D]">{t.filled}</div>
              </div>
              <div>
                <div className="small-label text-gray-500">MT</div>
                <div className="data-num text-2xl sm:text-3xl text-[#B91C1C]">{t.mt}</div>
              </div>
            </div>
          </div>
        ))}
        <div className="card-flat p-5" data-testid="kpi-pending-payments">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-label text-gray-500">Cash Flow</div>
              <div className="font-heading font-bold text-xl mt-1">Pending ₹</div>
            </div>
            <Wallet size={22} strokeWidth={2.5} className="text-gray-400" />
          </div>
          <div className={`grid ${isManager ? "grid-cols-1" : "grid-cols-2"} gap-3`}>
            <div>
              <div className="small-label text-gray-500">To receive</div>
              <div className="data-num text-2xl text-[#15803D]">₹ {formatMoney(pendingPay?.total_customer_due || 0)}</div>
            </div>
            {!isManager && (
              <div>
                <div className="small-label text-gray-500">To pay</div>
                <div className="data-num text-2xl text-[#B91C1C]">₹ {formatMoney(pendingPay?.total_vendor_due || 0)}</div>
              </div>
            )}
          </div>
          <Link to="/pending/payments" className="small-label text-gray-700 inline-flex items-center gap-1 mt-3 hover:text-black">
            View all <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Sales trend (last 7 days + this vs last month + target) */}
      <SalesTrendCard data={salesTrend} />

      {/* Live stock grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card-flat p-5" data-testid="kpi-total-purchase">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-label text-gray-500">All-time</div>
              <div className="font-heading font-bold text-xl mt-1">Total Purchase Qty</div>
            </div>
            <ArrowDownToLine size={22} strokeWidth={2.5} className="text-[#15803D]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="small-label text-gray-500">Cylinders</div>
              <div className="data-num text-2xl sm:text-3xl text-[#0A0A0A]" data-testid="purchase-cylinders">
                {totalsQty?.purchase?.cylinders ?? 0}
              </div>
            </div>
            <div>
              <div className="small-label text-gray-500">Total KG</div>
              <div className="data-num text-2xl sm:text-3xl text-[#15803D]" data-testid="purchase-kg">
                {formatMoney(totalsQty?.purchase?.kg ?? 0)} <span className="text-sm font-medium">kg</span>
              </div>
            </div>
          </div>
          {(totalsQty?.purchase?.items?.length || 0) > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-3 max-h-32 overflow-auto">
              <table className="table-dense w-full text-xs">
                <tbody>
                  {totalsQty.purchase.items.map((r, i) => (
                    <tr key={i}>
                      <td className="font-semibold">{r.brand}</td>
                      <td className="text-gray-600">{r.size}</td>
                      <td className="num">{r.cylinders}</td>
                      <td className="num text-[#15803D]">{formatMoney(r.kg)} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card-flat p-5" data-testid="kpi-total-sale">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="small-label text-gray-500">All-time</div>
              <div className="font-heading font-bold text-xl mt-1">Total Sale Qty</div>
            </div>
            <ArrowUpFromLine size={22} strokeWidth={2.5} className="text-[#B91C1C]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="small-label text-gray-500">Cylinders</div>
              <div className="data-num text-2xl sm:text-3xl text-[#0A0A0A]" data-testid="sale-cylinders">
                {totalsQty?.sale?.cylinders ?? 0}
              </div>
            </div>
            <div>
              <div className="small-label text-gray-500">Total KG</div>
              <div className="data-num text-2xl sm:text-3xl text-[#B91C1C]" data-testid="sale-kg">
                {formatMoney(totalsQty?.sale?.kg ?? 0)} <span className="text-sm font-medium">kg</span>
              </div>
            </div>
          </div>
          {(totalsQty?.sale?.items?.length || 0) > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-3 max-h-32 overflow-auto">
              <table className="table-dense w-full text-xs">
                <tbody>
                  {totalsQty.sale.items.map((r, i) => (
                    <tr key={i}>
                      <td className="font-semibold">{r.brand}</td>
                      <td className="text-gray-600">{r.size}</td>
                      <td className="num">{r.cylinders}</td>
                      <td className="num text-[#B91C1C]">{formatMoney(r.kg)} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Bullet (KG) stock */}
      {Object.keys(bulletStock).length > 0 && (
        <div className="card-flat">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="small-label text-gray-500">Bulk LPG</div>
              <h2 className="font-heading font-black text-xl sm:text-2xl">Bullet Stock · KG</h2>
            </div>
            <Flame size={22} strokeWidth={2.5} className="text-[#B45309]" />
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(bulletStock).map(([name, kg]) => {
              const value = Math.round(Number(kg || 0) * 100) / 100;
              const negative = value < 0;
              return (
                <div key={name} className={`card-flat p-4 ${negative ? "bg-[#FEE2E2] border-[#FECACA]" : "bg-[#FFF7ED] border-[#FED7AA]"}`} data-testid={`bullet-card-${name}`}>
                  <div className={`small-label flex items-center gap-1.5 ${negative ? "text-[#B91C1C]" : "text-[#B45309]"}`}>
                    <Flame size={12} /> {name}
                  </div>
                  <div className={`data-num text-3xl font-black mt-1 ${negative ? "text-[#B91C1C]" : "text-[#B45309]"}`}>
                    {formatMoney(value)} <span className="text-sm font-medium text-gray-600">KG</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {negative ? "below zero · check refills" : "current LPG in tank"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live stock grid */}
      <div className="card-flat">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="small-label text-gray-500">Live Stock</div>
            <h2 className="font-heading font-black text-xl sm:text-2xl">Every cylinder, right now</h2>
          </div>
          <Flame size={22} strokeWidth={2.5} className="text-gray-400" />
        </div>        <div className="overflow-auto max-h-[520px]">
          {loading && <div className="p-6 text-sm text-gray-500">Loading…</div>}
          {!loading && godowns.length === 0 && (
            <div className="p-8 text-center text-gray-500 text-sm">
              No stock yet. Start by adding Opening stock from <Link className="underline" to="/entry/adjustment">Adjustment</Link>
              {" "}or record a <Link className="underline" to="/entry/purchase">Purchase</Link>.
            </div>
          )}
          {godowns.map((g) => {
            const brands = Object.keys(stock[g] || {}).sort();
            const rows = [];
            for (const b of brands) {
              for (const s of Object.keys(stock[g][b]).sort((a, c) => parseFloat(a) - parseFloat(c))) {
                const v = stock[g][b][s];
                rows.push({ brand: b, size: s, filled: v.filled, mt: v.mt });
              }
            }
            return (
              <div key={g} className="border-t border-gray-200 first:border-t-0">
                <div className="px-5 py-3 bg-[#0A0A0A] text-white flex items-center justify-between sticky top-0">
                  <div className="font-heading font-bold tracking-wider uppercase text-sm">{g}</div>
                  <div className="text-xs text-gray-300">{rows.length} sku(s)</div>
                </div>
                <table className="table-dense w-full">
                  <thead>
                    <tr>
                      <th className="text-left">Brand</th>
                      <th className="text-left">Size</th>
                      <th className="num">Filled</th>
                      <th className="num">MT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} data-testid={`stock-row-${g}-${r.brand}-${r.size}`}>
                        <td className="font-semibold">{r.brand}</td>
                        <td>{r.size}</td>
                        <td className="num">
                          <span className={`inline-block px-2 py-0.5 rounded-sm ${r.filled > 0 ? "stock-filled" : "text-gray-400"}`}>
                            {r.filled}
                          </span>
                        </td>
                        <td className="num">
                          <span className={`inline-block px-2 py-0.5 rounded-sm ${r.mt > 0 ? "stock-mt" : r.mt < 0 ? "stock-mt" : "text-gray-400"}`}>
                            {r.mt}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan="4" className="text-center text-gray-400 py-4">No stock</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom: pending cylinders + recent txns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="small-label text-gray-500">Rotation</div>
              <h3 className="font-heading font-bold text-xl">Pending Cylinders</h3>
            </div>
            <FileWarning size={20} className="text-gray-400" />
          </div>
          <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr><th className="text-left">Party</th><th className="text-left">Type</th><th className="num">Total Pending</th></tr>
            </thead>
            <tbody>
              {(pendingCyl?.rows || []).slice(0, 6).map((r) => (
                <tr key={r.party_id}>
                  <td><Link to={`/parties/${r.party_id}`} className="hover:underline font-medium">{r.name}</Link></td>
                  <td className="capitalize">{r.type}</td>
                  <td className={`num ${r.total_pending > 0 ? "text-[#B45309]" : "text-gray-500"}`}>{r.total_pending}</td>
                </tr>
              ))}
              {(!pendingCyl?.rows || pendingCyl.rows.length === 0) && (
                <tr><td colSpan="3" className="py-4 text-center text-gray-400 text-sm">All clear 🎉</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="card-flat">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="small-label text-gray-500">Activity</div>
              <h3 className="font-heading font-bold text-xl">Recent Entries</h3>
            </div>
            <TrendingUp size={20} className="text-gray-400" />
          </div>
          <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th><th className="text-left">Type</th>
                <th className="text-left">Challan</th><th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {txns.slice(0, 8).map((t) => (
                <tr key={t.id}>
                  <td className="data-num text-xs">{t.date}</td>
                  <td><span className="capitalize">{t.type}</span></td>
                  <td className="data-num text-xs">{t.challan_no || "—"}</td>
                  <td className="num">
                    {isManager && t.type === "purchase"
                      ? <span className="text-gray-400 text-xs">hidden</span>
                      : (t.total_amount ? `₹ ${formatMoney(t.total_amount)}` : "—")}
                  </td>
                </tr>
              ))}
              {txns.length === 0 && (
                <tr><td colSpan="4" className="py-4 text-center text-gray-400 text-sm">No entries yet</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}
